// API Client for S3 Proxy Server
import { useKeycloak } from '../contexts/KeycloakContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

class ApiClient {
  constructor(token) {
    this.token = token;
    this.baseURL = API_BASE_URL;
    this.requestTimeout = 30000; // 30초 타임아웃
    this.requestCount = 0;
    this.lastRequestTime = 0;
    this.isBlocked = false;
  }

  async request(endpoint, options = {}) {
    // 요청 제한 확인
    const now = Date.now();
    
    // 1초 내에 5회 이상 요청 시 차단
    if (now - this.lastRequestTime < 1000) {
      this.requestCount++;
      if (this.requestCount > 5) {
        this.isBlocked = true;
        console.error('API client blocked due to excessive requests');
        throw new Error('너무 많은 요청으로 인해 API 호출이 차단되었습니다. 잠시 후 다시 시도해주세요.');
      }
    } else {
      this.requestCount = 1;
    }
    
    this.lastRequestTime = now;
    
    // 차단된 상태면 요청 거부
    if (this.isBlocked) {
      throw new Error('API 클라이언트가 차단된 상태입니다. 페이지를 새로고침해주세요.');
    }

    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
        ...options.headers
      },
      signal: AbortSignal.timeout(this.requestTimeout), // 타임아웃 설정
      ...options
    };

    try {
      console.log(`🌐 API Request: ${options.method || 'GET'} ${url} (count: ${this.requestCount})`);
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        
        // 상세한 에러 정보 로깅
        console.error(`❌ API Error Details:`, {
          status: response.status,
          statusText: response.statusText,
          url: url,
          errorData: errorData
        });
        
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log(`✅ API Response: ${options.method || 'GET'} ${url}`, data);
      
      // 성공 시 요청 카운트 리셋
      this.requestCount = 0;
      
      return data;
    } catch (error) {
      console.error(`❌ API Error: ${options.method || 'GET'} ${url}`, error);
      
      // 타임아웃 에러 처리
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        throw new Error('요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.');
      }
      
      // 네트워크 에러 처리
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('네트워크 연결에 실패했습니다. 인터넷 연결과 서버 상태를 확인해주세요.');
      }
      
      throw error;
    }
  }

  // S3 객체 목록 조회
  async listObjects(prefix = '') {
    const params = new URLSearchParams();
    if (prefix) params.append('prefix', prefix);
    
    return this.request(`/s3/list?${params.toString()}`);
  }

  // S3 객체 다운로드 URL 생성
  async getDownloadUrl(key) {
    const params = new URLSearchParams({ key });
    return this.request(`/s3/download?${params.toString()}`);
  }

  // S3 객체 정보 조회
  async getObjectInfo(key) {
    const params = new URLSearchParams({ key });
    return this.request(`/s3/info?${params.toString()}`);
  }

  // 사용자 정보 조회
  async getUserInfo() {
    return this.request('/s3/user');
  }

  // healthCheck 함수 완전 제거 - 무한 루프 방지
}

// React Hook for API Client
export const useApiClient = () => {
  const { token, isAuthenticated } = useKeycloak();
  
  if (!isAuthenticated || !token) {
    throw new Error('User must be authenticated to use API client');
  }
  
  return new ApiClient(token);
};

export default ApiClient;
